import { CLIENT_ERROR_CATEGORY } from "@/loggers/types";
import { categorizeError, shouldEmit, toErrorMessage, toErrorName, toErrorStack } from "./classify";
import { logClientError } from "./logClientError";

let registered = false;

/**
 * Categorises, dedupes and emits an error. Exported so non-window sources
 * (e.g. Vue's app.config.errorHandler) can route through the same pipeline.
 */
export const reportClientError = (error: unknown, fallback: CLIENT_ERROR_CATEGORY, source: string): void => {
  const category = categorizeError(error, fallback);
  const message = toErrorMessage(error);

  if (!shouldEmit(`${category}|${message}|${source}`)) {
    return;
  }

  logClientError(category, { message, source, errorName: toErrorName(error), stack: toErrorStack(error) });
};

/** URL of a failed resource element, used as the error source. */
const resourceUrl = (target: HTMLScriptElement | HTMLLinkElement): string =>
  target instanceof HTMLScriptElement ? target.src : target.href;

/**
 * Registers global listeners for uncaught runtime errors, unhandled promise
 * rejections and failed resource (chunk) loads. Idempotent — safe to call once
 * during app bootstrap.
 */
export const registerGlobalErrorHandlers = (): void => {
  if (registered || typeof window === "undefined") {
    return;
  }
  registered = true;

  // Bubble phase: uncaught runtime JS errors (target is window).
  window.addEventListener("error", (event: ErrorEvent) => {
    reportClientError(event.error ?? event.message, CLIENT_ERROR_CATEGORY.UNCAUGHT_JS, event.filename || "window");
  });

  // Capture phase: resource load failures (script/link) do not bubble, so they
  // are only observable here. JS errors (target = window) are ignored to avoid
  // double-counting the bubble-phase listener above.
  window.addEventListener(
    "error",
    (event: Event) => {
      const target = event.target;
      if (target instanceof HTMLScriptElement || target instanceof HTMLLinkElement) {
        const url = resourceUrl(target);
        reportClientError(`Failed to load resource: ${url}`, CLIENT_ERROR_CATEGORY.CHUNK_LOAD, url || "resource");
      }
    },
    true
  );

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    reportClientError(event.reason, CLIENT_ERROR_CATEGORY.UNHANDLED_REJECTION, "promise");
  });
};
